import { Droplets, Wind, MapPin, RefreshCw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useUserLocationWeather } from "@/hooks/useUserLocationWeather";

function formatAgo(ts: number | null): string {
  if (!ts) return "";
  const mins = Math.max(0, Math.round((Date.now() - ts) / 60000));
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min ago";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  return hrs === 1 ? "1 hr ago" : `${hrs} hrs ago`;
}

export function HomeWeatherSummary() {
  const { weather: geoWeather, fetchedAt: geoFetchedAt, loading: geoLoading, denied, refresh: refreshGeo } = useUserLocationWeather();

  const handleRefresh = () => {
    refreshGeo();
  };

  const getWeatherIconUrl = (icon: string) =>
    `https://openweathermap.org/img/wn/${icon}.png`;

  if (geoLoading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-2">
        <Skeleton className="h-14 w-48 flex-shrink-0 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-baseline gap-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Local Weather
          </p>
          {geoFetchedAt && (
            <span className="text-[10px] text-muted-foreground/70">
              Updated {formatAgo(geoFetchedAt)}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={geoLoading}
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          aria-label="Refresh weather"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${geoLoading ? "animate-spin" : ""}`} />
          <span className="ml-1 hidden sm:inline">Refresh</span>
        </Button>
      </div>
      {!geoWeather && (
        <div className="rounded-lg border border-dashed border-border bg-card/40 px-4 py-3 text-xs text-muted-foreground">
          {denied
            ? "Location access is blocked. Enable location permissions to see local weather."
            : "Local weather unavailable. Try refreshing or enable location services."}
        </div>
      )}
      <div className="flex gap-3 overflow-x-auto pb-1">
        {geoWeather && (
          <div
            key="user-location"
            className="flex items-center gap-3 rounded-lg border border-primary/40 bg-card/80 backdrop-blur-sm px-4 py-2.5 flex-shrink-0"
          >
            <img
              src={getWeatherIconUrl(geoWeather.icon)}
              alt={geoWeather.description}
              className="h-8 w-8"
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground flex items-center gap-1 truncate max-w-[140px]">
                <MapPin className="h-3 w-3 text-primary" />
                {geoWeather.city || "Your Location"}
              </p>
              <p className="text-xs text-muted-foreground capitalize">
                {geoWeather.description}
              </p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-foreground">
                {Math.round(geoWeather.temp)}°F
              </p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-0.5">
                  <Wind className="h-3 w-3" />
                  {Math.round(geoWeather.wind_speed)}
                </span>
                <span className="flex items-center gap-0.5">
                  <Droplets className="h-3 w-3" />
                  {geoWeather.humidity}%
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
